import posthog from 'posthog-js';

import { EventName, EventProperties } from '@/lib/tracking/types';
import { FrontendTracker } from '@/lib/tracking/utils/FrontendTracker';
import { isMarketingEvent } from '@/lib/tracking/utils/isMarketingEvent';
import { ServerSideTracker } from '@/lib/tracking/utils/ServerSideTracker';
import { Tracker } from '@/lib/tracking/utils/Tracker';

export class TrackingManager {
  private trackers: Tracker[] = [];
  private readonly ANON_ID_KEY = 'biteclub_anon_id';

  constructor() {
    // Always load the ServerSideTracker, regardless of consent
    this.addTracker(new ServerSideTracker());
  }

  private getAnonymousId(): string | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }

    // Try to get PostHog's distinct ID first
    if (posthog.__loaded) {
      const phDistinctId = posthog.get_distinct_id();
      if (phDistinctId) {
        return phDistinctId;
      }
    }

    // Fallback to our own localStorage ID
    try {
      let anonId = localStorage.getItem(this.ANON_ID_KEY);
      if (!anonId) {
        anonId = crypto.randomUUID();
        localStorage.setItem(this.ANON_ID_KEY, anonId);
      }
      return anonId;
    } catch {
      // localStorage unavailable (Safari private mode, storage quota, security policy)
      // Fall back to in-memory UUID — not persisted, but tracking stays functional
      return crypto.randomUUID();
    }
  }

  // ADD: Error capture method
  captureException(error: Error, properties?: Record<string, any>): void {
    this.trackers.forEach((tracker) => {
      if (tracker instanceof FrontendTracker) {
        tracker.captureException(error, properties);
      }
    });
  }

  enableTracking() {
    if (!this.trackers.some((tracker) => tracker instanceof FrontendTracker)) {
      this.addTracker(new FrontendTracker());
      // Add other trackers as needed
    }
  }

  disableTracking() {
    this.trackers.forEach((tracker) => {
      if (tracker instanceof FrontendTracker) {
        posthog.reset();
      }
    });
    this.trackers = [];
  }

  addTracker(tracker: Tracker) {
    this.trackers.push(tracker);
    tracker.init();
  }

  trackEvent<T extends EventName>(event: T, properties: EventProperties<T>) {
    // Marketing events go through posthog-js, which keeps its own id; skipping ours means a
    // landing visitor who has not accepted cookies gets no id written to localStorage.
    const anonymousId = isMarketingEvent(event) ? undefined : this.getAnonymousId();
    this.trackers.forEach((tracker) => {
      if (tracker.trackEvent) {
        tracker.trackEvent(event, properties, anonymousId);
      }
    });
  }

  identify(traits?: Record<string, any>) {
    const anonymousId = this.getAnonymousId();
    this.trackers.forEach((tracker) => tracker.identify(traits, anonymousId));
  }

  reset() {
    this.trackers.forEach((tracker) => {
      if (tracker.reset) {
        tracker.reset();
      }
    });

    // FrontendTracker.reset() rotates PostHog's own device ID. Our fallback ID,
    // used whenever PostHog has not loaded, has to go with it — otherwise the
    // next person on this device inherits the previous one's anonymous identity.
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(this.ANON_ID_KEY);
      } catch {
        // localStorage unavailable (Safari private mode, storage quota, security
        // policy). Nothing was persisted, so there is nothing to remove.
      }
    }
  }
}
